import React, { useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';

export const StorageTest: React.FC = () => {
  const { isAdmin } = useAuth();
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState<string[]>([]);

  const log = (message: string) => {
    setResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testStorage = async () => {
    setTesting(true);
    setResults([]);
    
    try {
      log('Testing storage bucket access...');
      
      // Test 1: Try to access the plugin-files bucket directly
      log('Testing direct bucket access...');
      try {
        const { data: files, error: directError } = await supabase.storage
          .from('plugin-files')
          .list('', { limit: 1 });
        
        if (directError) {
          log(`❌ Direct bucket access failed: ${directError.message}`);
        } else {
          log('✅ Direct bucket access successful');
          log(`✅ plugin-files bucket is accessible (found ${files?.length || 0} files)`);
        }
      } catch (err) {
        log(`❌ Direct bucket access error: ${err}`);
      }
      
      // Test 2: List buckets (might fail due to permissions)
      log('Testing bucket listing...');
      try {
        const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
        
        if (bucketsError) {
          log(`⚠️ Cannot list buckets (permission issue): ${bucketsError.message}`);
          log('This is normal - your bucket exists but listing requires special permissions');
        } else {
          log(`✅ Found ${buckets?.length || 0} buckets via listing`);
          buckets?.forEach(bucket => {
            log(`  - ${bucket.id} (${bucket.name})`);
          });
        }
      } catch (err) {
        log(`⚠️ Bucket listing error: ${err}`);
      }
      
      // Test 3: Try to upload a test file
      log('Testing file upload...');
      const testFile = new File(['Test content for storage'], 'test-upload.txt', { 
        type: 'text/plain' 
      });
      
      const testPath = `test/storage-test-${Date.now()}.txt`;
      const { error: uploadError } = await supabase.storage
        .from('plugin-files')
        .upload(testPath, testFile);
      
      if (uploadError) {
        log(`❌ Upload failed: ${uploadError.message}`);
        
        if (uploadError.message.includes('The resource was not found')) {
          log('💡 This means the bucket exists but there are permission issues');
          log('💡 Try running the storage policies fix SQL script');
        }
      } else {
        log('✅ Upload test successful');
        
        // Test 4: Try to create signed URL
        log('Testing signed URL generation...');
        const { data: signedUrl, error: signedError } = await supabase.storage
          .from('plugin-files')
          .createSignedUrl(testPath, 300);
        
        if (signedError) {
          log(`❌ Signed URL failed: ${signedError.message}`);
        } else {
          log('✅ Signed URL generation successful');
          log(`✅ Download URL: ${signedUrl.signedUrl}`);
        }
        
        // Clean up test file
        const { error: deleteError } = await supabase.storage
          .from('plugin-files')
          .remove([testPath]);
        
        if (deleteError) {
          log(`⚠️ Cleanup failed: ${deleteError.message}`);
        } else {
          log('✅ Test file cleaned up');
        }
      }
      
      // Test 5: Check existing plugin files
      log('Checking for existing plugin files...');
      const { data: pluginFiles, error: pluginFilesError } = await supabase.storage
        .from('plugin-files')
        .list('', { limit: 10 });
      
      if (pluginFilesError) {
        log(`❌ Cannot list plugin files: ${pluginFilesError.message}`);
      } else {
        log(`✅ Found ${pluginFiles?.length || 0} plugin files`);
        if (pluginFiles && pluginFiles.length > 0) {
          pluginFiles.forEach(file => {
            log(`  - ${file.name} (${file.metadata?.size || 'unknown'} bytes)`);
          });
        }
      }
      
      log('✅ Storage diagnostic completed!');
      log('');
      log('📋 Summary:');
      log('• If direct bucket access works, your bucket is properly set up');
      log('• If uploads work, you can upload new plugin versions');
      log('• If signed URLs work, downloads will work properly');
      log('• If bucket listing fails, that\'s normal - it requires admin privileges');
      
    } catch (error) {
      log(`❌ Test error: ${error}`);
    } finally {
      setTesting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-semibold text-red-800 mb-2">Access Denied</h3>
        <p className="text-red-600">This storage test is only available to administrators.</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-xl font-bold mb-4">Storage Bucket Test</h2>
      <p className="text-gray-600 mb-4">
        This tool tests if the plugin-files storage bucket is working properly.
      </p>
      
      <button
        onClick={testStorage}
        disabled={testing}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 mb-4"
      >
        {testing ? 'Testing Storage...' : 'Test Storage'}
      </button>
      
      {results.length > 0 && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Test Results:</h3>
          <div className="bg-gray-100 p-4 rounded max-h-96 overflow-y-auto">
            {results.map((result, index) => (
              <div
                key={index}
                className={`text-sm font-mono mb-1 ${
                  result.includes('❌') ? 'text-red-600' :
                  result.includes('✅') ? 'text-green-600' :
                  result.includes('💡') ? 'text-blue-600' :
                  'text-gray-700'
                }`}
              >
                {result}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 